import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { validateImageFile, createFilePreview } from "./utils/photoHelpers";
import { findPhotoByTemplate } from "./utils/photoHelpers";
import { handleDragOver, handleDragLeave, handleMultiFileDrop } from "./utils/dragAndDrop";
import "./bulkPhotoUploadModal.css";

function BulkPhotoUpload({
    isOpen,
    onClose,
    photoTemplate,
    existingPhotos,
    uploadedPhotos,
    onAssignPhotos,
    uploading = false
}) {
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [validationErrors, setValidationErrors] = useState({});
    // assignments: { templateName -> fileId }
    const [assignments, setAssignments] = useState({});
    const [draggingFileId, setDraggingFileId] = useState(null);
    const [dragOverSlot, setDragOverSlot] = useState(null);
    const [lightboxFileId, setLightboxFileId] = useState(null);
    const [openCategories, setOpenCategories] = useState({ exterior: true, interior: false, detail: false });
    const [showDropZone, setShowDropZone] = useState(true);
    const didDragRef = useRef(false);
    const stripRef = useRef(null);

    // The parent keeps this component mounted and just toggles isOpen, so internal
    // state (selected files, assignments) otherwise survives a close — clear it here
    // whenever the modal closes, whether from Cancel/X or after a successful upload.
    useEffect(() => {
        if (!isOpen) {
            setSelectedFiles([]);
            setValidationErrors({});
            setAssignments({});
            setShowDropZone(true);
        }
    }, [isOpen]);

    const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const processFiles = useCallback(async (fileList) => {
        const files = Array.from(fileList);
        const newEntries = [];
        const errors = {};

        for (const file of files) {
            const id = generateId();
            const validation = validateImageFile(file);

            if (!validation.valid) {
                errors[id] = validation.error;
                newEntries.push({ id, file, preview: null, invalid: true });
                continue;
            }

            try {
                const preview = await createFilePreview(file);
                newEntries.push({ id, file, preview, invalid: false });
            } catch {
                newEntries.push({ id, file, preview: null, invalid: true });
                errors[id] = 'Failed to generate preview';
            }
        }

        setSelectedFiles(prev => [...prev, ...newEntries]);
        setValidationErrors(prev => ({ ...prev, ...errors }));
        setShowDropZone(false);
    }, []);

    const handleFileInput = useCallback((e) => {
        if (e.target.files && e.target.files.length > 0) {
            processFiles(e.target.files);
        }
        e.target.value = '';
    }, [processFiles]);

    const handleZoneDrop = useCallback((e) => {
        // Ignore internal strip-to-slot drags — those carry fileId, not filesystem files
        if (e.dataTransfer.getData('fileId') || draggingFileId) return;
        const files = handleMultiFileDrop(e);
        if (files) processFiles(files);
    }, [processFiles, draggingFileId]);

    const removeFile = useCallback((fileId) => {
        setSelectedFiles(prev => {
            const updated = prev.filter(f => f.id !== fileId);
            if (updated.length === 0) setShowDropZone(true);
            return updated;
        });
        setValidationErrors(prev => {
            const updated = { ...prev };
            delete updated[fileId];
            return updated;
        });
        // Remove any assignment that referenced this file
        setAssignments(prev => {
            const updated = { ...prev };
            Object.keys(updated).forEach(k => {
                if (updated[k] === fileId) delete updated[k];
            });
            return updated;
        });
    }, []);

    const clearAll = useCallback(() => {
        setSelectedFiles([]);
        setValidationErrors({});
        setAssignments({});
        setShowDropZone(true);
    }, []);

    const handleClose = useCallback(() => {
        setSelectedFiles([]);
        setValidationErrors({});
        setAssignments({});
        setShowDropZone(true);
        onClose();
    }, [onClose]);

    // ── Drag from strip ──────────────────────────────────────────────────────
    const handleStripDragStart = useCallback((e, fileId) => {
        didDragRef.current = true;
        setDraggingFileId(fileId);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', fileId);
        e.dataTransfer.setData('fileId', fileId);
    }, []);

    const handleStripDragEnd = useCallback(() => {
        setDraggingFileId(null);
        setDragOverSlot(null);
        // Reset after a tick so the click that fires after dragend is suppressed
        setTimeout(() => { didDragRef.current = false; }, 0);
    }, []);

    // ── Drop onto slot ────────────────────────────────────────────────────────
    const handleSlotDragOver = useCallback((e, templateName) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverSlot(templateName);
    }, []);

    const handleSlotDragLeave = useCallback(() => {
        setDragOverSlot(null);
    }, []);

    const handleSlotDrop = useCallback((e, templateName) => {
        e.preventDefault();
        e.stopPropagation();
        const fileId = e.dataTransfer.getData('fileId') || e.dataTransfer.getData('text/plain');
        setDragOverSlot(null);
        if (!fileId) return;

        setAssignments(prev => {
            const updated = { ...prev };
            Object.keys(updated).forEach(k => {
                if (updated[k] === fileId) delete updated[k];
            });
            updated[templateName] = fileId;
            return updated;
        });
    }, []);

    const clearSlotAssignment = useCallback((templateName) => {
        setAssignments(prev => {
            const updated = { ...prev };
            delete updated[templateName];
            return updated;
        });
    }, []);

    // ── Available slots (not already filled) ─────────────────────────────────
    // Multi-photo templates expand into one virtual slot per unfilled sub-tag.
    // Each virtual slot carries { ...template, slotKey, subTag } so the grid
    // and lightbox can treat them uniformly.
    const availableTemplates = useMemo(() => {
        const result = [];
        photoTemplate.forEach(t => {
            if (t.multiPhoto && t.subTags) {
                t.subTags.forEach(subTag => {
                    const slotKey = `${t.name}::${subTag}`;
                    const hasExisting = findPhotoByTemplate(existingPhotos, t, subTag);
                    const hasStaged = uploadedPhotos[slotKey];
                    if (!hasExisting && !hasStaged) {
                        result.push({ ...t, slotKey, activeSubTag: subTag });
                    }
                });
            } else {
                const hasExisting = findPhotoByTemplate(existingPhotos, t);
                const hasStaged = uploadedPhotos[t.name];
                if (!hasExisting && !hasStaged) {
                    result.push({ ...t, slotKey: t.name, activeSubTag: null });
                }
            }
        });
        return result;
    }, [photoTemplate, existingPhotos, uploadedPhotos]);

    const templatesByCategory = useMemo(() => {
        const map = { exterior: [], interior: [], detail: [] };
        availableTemplates.forEach(t => {
            map[t.category]?.push(t);
        });
        return map;
    }, [availableTemplates]);

    const assignedCount = useMemo(() => Object.keys(assignments).length, [assignments]);

    const fileById = useMemo(() => {
        const map = {};
        selectedFiles.forEach(f => { map[f.id] = f; });
        return map;
    }, [selectedFiles]);

    const handleConfirm = useCallback(() => {
        const assignmentList = Object.entries(assignments)
            .map(([slotKey, fileId]) => {
                const file = fileById[fileId];
                const sepIdx = slotKey.indexOf('::');
                const templateName = sepIdx !== -1 ? slotKey.slice(0, sepIdx) : slotKey;
                const subTag = sepIdx !== -1 ? slotKey.slice(sepIdx + 2) : null;
                const template = photoTemplate.find(t => t.name === templateName);
                if (!file || !template) return null;
                return { file: file.file, template, subTag };
            })
            .filter(Boolean);

        // Parent controls closing — it will close after upload completes
        onAssignPhotos(assignmentList);
    }, [assignments, fileById, photoTemplate, onAssignPhotos]);

    if (!isOpen) return null;

    const categoryLabels = { exterior: 'Exterior', interior: 'Interior', detail: 'Details' };

    return (
        <div className="bulk-modal-overlay" onClick={handleClose}>
            <div className="bulk-modal bulk-modal-wide" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="bulk-modal-header">
                    <h3 className="bulk-modal-title">Bulk Photo Upload</h3>
                    <button className="bulk-modal-close" onClick={handleClose}>&times;</button>
                </div>

                <div className="bulk-modal-body">
                    {/* ── Step 1: Select photos ─────────────────────────────── */}
                    <div className="bulk-step-label">
                        <span className="bulk-step-num">1</span>
                        Select photos
                        <span className="bulk-step-hint">
                            {selectedFiles.length > 0
                                ? `${selectedFiles.length} selected`
                                : `${availableTemplates.length} slots available`}
                        </span>
                    </div>

                    {showDropZone || selectedFiles.length === 0 ? (
                        <div
                            className="bulk-drop-zone"
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleZoneDrop}
                        >
                            <input
                                type="file"
                                id="bulk-photo-upload-input"
                                name="bulkPhotos"
                                multiple
                                accept="image/jpeg,image/jpg,image/png,image/webp"
                                onChange={handleFileInput}
                            />
                            <p className="bulk-drop-icon">+</p>
                            <p>Drag photos here or click to browse</p>
                        </div>
                    ) : (
                        <button
                            type="button"
                            className="bulk-add-more-button"
                            onClick={() => setShowDropZone(true)}
                        >
                            + Add More Photos
                        </button>
                    )}

                    {/* ── Selected photos strip ─────────────────────────────── */}
                    {selectedFiles.length > 0 && (
                        <>
                            <div className="bulk-strip-header">
                                <span>{selectedFiles.length} photo{selectedFiles.length !== 1 ? 's' : ''} selected — drag onto a slot below to assign</span>
                                <button className="bulk-strip-clear" onClick={clearAll}>Clear All</button>
                            </div>
                            <div className="bulk-photo-strip" ref={stripRef}>
                                {selectedFiles.map(f => {
                                    const isAssigned = Object.values(assignments).includes(f.id);
                                    const isDragging = draggingFileId === f.id;
                                    return (
                                        <div
                                            key={f.id}
                                            className={`bulk-strip-item${f.invalid ? ' invalid' : ''}${isAssigned ? ' assigned' : ''}${isDragging ? ' dragging' : ''}`}
                                            draggable={!f.invalid}
                                            onDragStart={e => handleStripDragStart(e, f.id)}
                                            onDragEnd={handleStripDragEnd}
                                            title={f.file.name}
                                        >
                                            {f.preview ? (
                                                <img
                                                    src={f.preview}
                                                    alt=""
                                                    className="bulk-strip-thumb"
                                                />
                                            ) : (
                                                <div className="bulk-strip-thumb bulk-strip-placeholder">?</div>
                                            )}
                                            {isAssigned && <div className="bulk-strip-assigned-badge">✓</div>}
                                            {!f.invalid && (
                                                <div className="bulk-strip-drag-handle" title="Drag to assign">⠿</div>
                                            )}
                                            <div className="bulk-strip-name">{f.file.name.replace(/\.[^/.]+$/, '')}</div>
                                            {f.invalid && (
                                                <div className="bulk-strip-error">{validationErrors[f.id]}</div>
                                            )}
                                            <button
                                                className="bulk-strip-remove"
                                                onClick={() => removeFile(f.id)}
                                                title="Remove"
                                            >&times;</button>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}

                    {/* ── Step 2: Assignment grid ───────────────────────────── */}
                    {selectedFiles.length > 0 && availableTemplates.length > 0 && (
                        <>
                            <div className="bulk-step-label" style={{ marginTop: 24 }}>
                                <span className="bulk-step-num">2</span>
                                Drop photos into slots
                                {assignedCount > 0 && (
                                    <span className="bulk-step-hint">{assignedCount} assigned</span>
                                )}
                            </div>

                            {Object.entries(templatesByCategory).map(([category, templates]) => {
                                if (templates.length === 0) return null;
                                const isOpen = openCategories[category];
                                const filledInCategory = templates.filter(t => assignments[t.slotKey]).length;
                                return (
                                    <div key={category} className="bulk-category-section">
                                        <button
                                            type="button"
                                            className="bulk-category-header"
                                            onClick={() => setOpenCategories(prev => ({ ...prev, [category]: !prev[category] }))}
                                        >
                                            <span className="bulk-category-title">{categoryLabels[category]}</span>
                                            <span className="bulk-category-count">
                                                {filledInCategory > 0 ? `${filledInCategory}/${templates.length} assigned` : `${templates.length} slots`}
                                            </span>
                                            <span className="bulk-category-chevron">{isOpen ? '▲' : '▼'}</span>
                                        </button>
                                        {isOpen && (
                                        <div className="bulk-slot-grid">
                                            {templates.map(template => {
                                                const slotKey = template.slotKey;
                                                const assignedFileId = assignments[slotKey];
                                                const assignedFile = assignedFileId ? fileById[assignedFileId] : null;
                                                const isOver = dragOverSlot === slotKey;

                                                return (
                                                    <div
                                                        key={slotKey}
                                                        className={`bulk-slot${isOver ? ' drag-over' : ''}${assignedFile ? ' filled' : ''}`}
                                                    >
                                                        {assignedFile ? (
                                                            <div className="bulk-slot-filled">
                                                                <img
                                                                    src={assignedFile.preview}
                                                                    alt=""
                                                                    className="bulk-slot-preview"
                                                                    onClick={() => setLightboxFileId(assignedFile.id)}
                                                                />
                                                                <div className="bulk-slot-filled-label title-direction">
                                                                    {template.name}
                                                                    {template.activeSubTag && (
                                                                        <span className="bulk-slot-subtag"> · {template.activeSubTag}</span>
                                                                    )}
                                                                    {template.icon && <span className="direction-icon"><template.icon /></span>}
                                                                </div>
                                                                <button
                                                                    className="bulk-slot-clear"
                                                                    onClick={() => clearSlotAssignment(slotKey)}
                                                                    title="Remove assignment"
                                                                >&times;</button>
                                                            </div>
                                                        ) : (
                                                            <div
                                                                className={`bulk-slot-dropzone${isOver ? ' active' : ''}`}
                                                                style={template.sampleImage ? {
                                                                    backgroundImage: `url(${template.sampleImage})`,
                                                                    backgroundSize: 'cover',
                                                                    backgroundPosition: 'center',
                                                                } : undefined}
                                                                onDragOver={e => handleSlotDragOver(e, slotKey)}
                                                                onDragLeave={handleSlotDragLeave}
                                                                onDrop={e => handleSlotDrop(e, slotKey)}
                                                            >
                                                                <div className="bulk-slot-label title-direction">
                                                                    {template.name}
                                                                    {template.activeSubTag && (
                                                                        <span className="bulk-slot-subtag"> · {template.activeSubTag}</span>
                                                                    )}
                                                                    {template.icon && <span className="direction-icon"><template.icon /></span>}
                                                                </div>
                                                                <span className="bulk-slot-drop-hint">
                                                                    {isOver ? 'Release to assign' : 'Drop here'}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        )}
                                    </div>
                                );
                            })}
                        </>
                    )}

                    {selectedFiles.length > 0 && availableTemplates.length === 0 && (
                        <p className="bulk-all-filled">All photo slots are already filled for this vehicle.</p>
                    )}
                </div>

                {/* Footer */}
                <div className="bulk-modal-footer">
                    {uploading ? (
                        <span className="bulk-footer-uploading">
                            <span className="bulk-footer-spinner" /> Uploading photos…
                        </span>
                    ) : assignedCount > 0 ? (
                        <span className="bulk-footer-summary">
                            {assignedCount} photo{assignedCount !== 1 ? 's' : ''} ready to upload
                        </span>
                    ) : null}
                    <button className="bulk-modal-cancel" onClick={handleClose} disabled={uploading}>Cancel</button>
                    <button
                        className="bulk-modal-confirm"
                        onClick={handleConfirm}
                        disabled={assignedCount === 0 || uploading}
                    >
                        {uploading
                            ? <><span className="bulk-confirm-spinner" /> Uploading…</>
                            : `Upload ${assignedCount > 0 ? assignedCount : ''} Photo${assignedCount !== 1 ? 's' : ''}`}
                    </button>
                </div>
            </div>

            {/* Lightbox with slot assignment */}
            {lightboxFileId && (() => {
                const lbFile = fileById[lightboxFileId];
                if (!lbFile) return null;
                const currentSlot = Object.keys(assignments).find(k => assignments[k] === lightboxFileId) || null;
                return (
                    <div className="bulk-lightbox-overlay" onClick={() => setLightboxFileId(null)}>
                        <div className="bulk-lightbox-panel" onClick={e => e.stopPropagation()}>
                            <button className="bulk-lightbox-close" onClick={() => setLightboxFileId(null)}>&times;</button>
                            <img src={lbFile.preview} alt="Preview" className="bulk-lightbox-img" />
                            <div className="bulk-lightbox-assign">
                                <div className="bulk-lightbox-assign-title">
                                    Assign to slot
                                    {currentSlot && <span className="bulk-lightbox-current-slot"> — currently: {currentSlot}</span>}
                                </div>
                                {Object.entries(templatesByCategory).map(([category, templates]) => {
                                    if (templates.length === 0) return null;
                                    const isOpen = openCategories[category];
                                    return (
                                        <div key={category} className="bulk-lightbox-accordion">
                                            <button
                                                className="bulk-lightbox-accordion-header"
                                                onClick={() => setOpenCategories(prev => ({ ...prev, [category]: !prev[category] }))}
                                            >
                                                <span>{categoryLabels[category]}</span>
                                                <span className="bulk-lightbox-accordion-count">{templates.length} slots</span>
                                                <span className="bulk-lightbox-accordion-chevron">{isOpen ? '▲' : '▼'}</span>
                                            </button>
                                            {isOpen && (
                                                <div className="bulk-lightbox-slot-list">
                                                    {templates.map(template => {
                                                        const slotKey = template.slotKey;
                                                        const slotOwner = assignments[slotKey];
                                                        const isThisFile = slotOwner === lightboxFileId;
                                                        const isTakenByOther = !!(slotOwner && slotOwner !== lightboxFileId);
                                                        const Icon = template.icon;
                                                        return (
                                                            <button
                                                                key={slotKey}
                                                                disabled={isTakenByOther}
                                                                className={`bulk-lightbox-slot-row${isThisFile ? ' active' : ''}${isTakenByOther ? ' taken' : ''}`}
                                                                onClick={() => {
                                                                    if (isThisFile) {
                                                                        clearSlotAssignment(slotKey);
                                                                    } else {
                                                                        setAssignments(prev => {
                                                                            const updated = { ...prev };
                                                                            Object.keys(updated).forEach(k => {
                                                                                if (updated[k] === lightboxFileId) delete updated[k];
                                                                            });
                                                                            updated[slotKey] = lightboxFileId;
                                                                            return updated;
                                                                        });
                                                                        setLightboxFileId(null);
                                                                    }
                                                                }}
                                                            >
                                                                <div className="bulk-lightbox-slot-row-left">
                                                                    {template.sampleImage
                                                                        ? <img src={template.sampleImage} alt="" className="bulk-lightbox-slot-sample" />
                                                                        : Icon
                                                                            ? <span className="bulk-lightbox-slot-icon"><Icon size={18} /></span>
                                                                            : <span className="bulk-lightbox-slot-icon-placeholder" />
                                                                    }
                                                                    <div className="bulk-lightbox-slot-info">
                                                                        <span className="bulk-lightbox-slot-name title-direction">
                                                                            {template.name}
                                                                            {template.activeSubTag && <span className="bulk-lightbox-slot-subtag"> · {template.activeSubTag}</span>}
                                                                            {template.icon && <span className="direction-icon"><template.icon /></span>}
                                                                        </span>
                                                                        <span className="bulk-lightbox-slot-desc">{template.description}</span>
                                                                    </div>
                                                                </div>
                                                                <div className="bulk-lightbox-slot-row-right">
                                                                    {isThisFile && <span className="bulk-lightbox-slot-assigned">Assigned ✓</span>}
                                                                    {isTakenByOther && <span className="bulk-lightbox-slot-taken-label">Taken</span>}
                                                                </div>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
}

export default BulkPhotoUpload;
